import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { clientPortalService } from '@services/clientPortalService';
import type { ClientPortalSession } from '@/types';
import {
  ensureProjectBucket,
  isProjectBucketFresh,
  markBucketLoading,
  type ProjectBucket,
} from '../utils/projectBucket';
import type { RootState } from '../index';

interface ProjectPortalBucket extends ProjectBucket {
  data: ClientPortalSession | null;
}

interface ProjectPortalState {
  byProjectId: Record<string, ProjectPortalBucket>;
}

const createBucket = (): ProjectPortalBucket => ({
  data: null,
  isLoading: false,
  error: null,
  lastFetched: null,
});

const initialState: ProjectPortalState = {
  byProjectId: {},
};

export const fetchProjectPortal = createAsyncThunk(
  'projectPortal/fetchProjectPortal',
  async (
    { projectId, force }: { projectId: string; force?: boolean },
    { getState, rejectWithValue }
  ) => {
    const state = getState() as RootState;
    const bucket = state.projectPortal.byProjectId[projectId];

    if (isProjectBucketFresh(bucket, force)) {
      return { projectId, data: null as ClientPortalSession | null };
    }

    try {
      const data = await clientPortalService.getProjectPortal(projectId);
      return { projectId, data };
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : 'Unable to load the stored client portal session for this project.';
      return rejectWithValue({ projectId, message });
    }
  }
);

const projectPortalSlice = createSlice({
  name: 'projectPortal',
  initialState,
  reducers: {
    // Keep the cached portal session in sync after an invoice mutation.
    setProjectPortal: (
      state,
      action: PayloadAction<{ projectId: string; data: ClientPortalSession }>
    ) => {
      const bucket = ensureProjectBucket(state.byProjectId, action.payload.projectId, createBucket);
      bucket.data = action.payload.data;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchProjectPortal.pending, (state, action) => {
        const bucket = ensureProjectBucket(
          state.byProjectId,
          action.meta.arg.projectId,
          createBucket
        );
        markBucketLoading(bucket);
      })
      .addCase(fetchProjectPortal.fulfilled, (state, action) => {
        const bucket = ensureProjectBucket(
          state.byProjectId,
          action.payload.projectId,
          createBucket
        );
        bucket.isLoading = false;
        if (action.payload.data !== null) {
          bucket.data = action.payload.data;
          bucket.lastFetched = Date.now();
        }
      })
      .addCase(fetchProjectPortal.rejected, (state, action) => {
        const payload = action.payload as { projectId?: string; message?: string } | undefined;
        const projectId = payload?.projectId ?? action.meta.arg.projectId;
        const bucket = ensureProjectBucket(state.byProjectId, projectId, createBucket);
        bucket.isLoading = false;
        bucket.data = null;
        bucket.error =
          payload?.message ?? 'Unable to load the stored client portal session for this project.';
      });
  },
});

export const { setProjectPortal } = projectPortalSlice.actions;

export const selectProjectPortal = (state: RootState, projectId: string) =>
  state.projectPortal.byProjectId[projectId]?.data ?? null;

export const selectProjectPortalLoading = (state: RootState, projectId: string) =>
  state.projectPortal.byProjectId[projectId]?.isLoading ?? false;

export const selectProjectPortalError = (state: RootState, projectId: string) =>
  state.projectPortal.byProjectId[projectId]?.error ?? null;

export default projectPortalSlice.reducer;
